import React from 'react';
import useClassrooms from 'state/hooks/use-classrooms';
import useReports from 'state/hooks/use-reports';
import { Typography, TypographyProps } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FONT_GSS_FUTURA_BOLD } from '@monorepo/shared/src/themes/defaultTheme';
import { remCalc } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import { translate } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import { UseReportsProps } from 'state/providers/ReportsProvider/types';
import { getLetters } from '@monorepo/shared/src/util/text-util';
import { getSelectedGroups, getSelectedStudents } from '../../utils/reports-util';
import { Classroom } from '@monorepo/shared/src/graph/schema-generated-types-educator';

const t = (key: string, options?) => translate('reports', key, options);

const ReportTitle: React.FC<TypographyProps> = (props) => {
  const classes = useStyles();
  const [{ activeClassroom }] = useClassrooms();
  const [reportState] = useReports();
  const reportTitle = getReportTitle(reportState, activeClassroom);

  return (
    <Typography variant="h2" className={classes.reportTitle} {...props}>
      {reportTitle}
    </Typography>
  );
};

const useStyles = makeStyles(() => ({
  reportTitle: {
    fontSize: remCalc(32),
    lineHeight: remCalc(48),
    fontFamily: FONT_GSS_FUTURA_BOLD,
  },
}));

const getReportTitle = (reportState: UseReportsProps, classroom: Classroom): string => {
  const filters = reportState.filters;

  const selectedStudent = reportState?.selectedStudent;
  const groups: any[] = getSelectedGroups(classroom, filters.studentIds);
  const students: any[] = getSelectedStudents(classroom, filters.studentIds);
  const letter = filters.character && getLetters(filters.character);
  const characterPart = letter && !filters.selectedStudentId ? ` - ${t('letter', { letter })}` : '';

  const hasSelectedStudents = students?.length > 0;
  const hasSelectedGroups = groups?.length > 0;
  const onlyOneSelectedStudent = hasSelectedStudents && students.length === 1;
  const onlyOneGroupSelected = hasSelectedGroups && groups?.length === 1;

  const getStudentTitle = () => {
    if (selectedStudent) return `${selectedStudent.firstName} ${selectedStudent.lastName}`;

    if (hasSelectedStudents) {
      if (onlyOneSelectedStudent) return `${students[0].firstName} ${students[0].lastName}`;
      if (hasSelectedGroups) return `${students?.length} Students`;
      return `Selected ${students?.length} Students`;
    }
  };

  const getGroupTitle = () => {
    const groupTitle = onlyOneGroupSelected ? groups[0].name : `Selected ${groups.length} Groups`;

    if (selectedStudent || hasSelectedStudents) {
      if (onlyOneGroupSelected && selectedStudent && groups[0].groupEnrollments?.length === 1) {
        return groupTitle;
      }
      return `${groupTitle} and ${getStudentTitle()}`;
    }

    return groupTitle;
  };

  const getReportTitle = () => {
    const studentTitle = getStudentTitle();

    if (selectedStudent && !hasSelectedGroups) return studentTitle;

    if (filters.studentIds.length === classroom?.students?.length) return classroom.name;

    if (hasSelectedGroups) return getGroupTitle();

    if (hasSelectedStudents) return studentTitle;

    return classroom.name;
  };

  const reportTitle = getReportTitle();

  return `${reportTitle} ${characterPart}`;
};

export default ReportTitle;
